import { createServerFn } from "@tanstack/react-start";
import { zodValidator } from "@tanstack/zod-adapter";
import { authMiddleware } from "~/server/middleware/auth";
import {
  type AuthContext,
  buildUrl,
  getHeaders,
  isMockMode,
  mockDelay,
} from "~/server/utils";
import type {
  SurveyAuthoringProject,
  SurveyAuthoringProjectSummary,
  SurveyAuthoringRevision,
  SurveyAuthoringRevisionDetail,
  SurveyAuthoringRevisionSummary,
} from "~/types/surveyAuthoring";
import {
  CreateSurveyAuthoringProjectSchema,
  CreateSurveyAuthoringRevisionSchema,
  SaveSurveyAuthoringDraftSchema,
  SurveyAuthoringProjectIdSchema,
  SurveyAuthoringRevisionIdSchema,
  SurveyAuthoringTeamSchema,
} from "~/types/surveyAuthoring";
import { handleApiResponse } from "../fetchUtils";

export const fetchSurveyAuthoringProjectsServerFn = createServerFn({
  method: "GET",
})
  .middleware([authMiddleware])
  .inputValidator(zodValidator(SurveyAuthoringTeamSchema))
  .handler(
    async ({ data, context }): Promise<SurveyAuthoringProjectSummary[]> => {
      if (isMockMode()) {
        const { listMockSurveyProjects } = await import(
          "~/mock/surveyAuthoring"
        );
        await mockDelay();
        return listMockSurveyProjects(data.team);
      }

      const { backendUrl, oboToken } = context as AuthContext;
      const response = await fetch(
        buildUrl(backendUrl, "/api/v1/intern/authoring/projects", {
          team: data.team,
        }),
        { headers: getHeaders(oboToken) },
      );
      await handleApiResponse(response);
      return response.json() as Promise<SurveyAuthoringProjectSummary[]>;
    },
  );

export const fetchSurveyAuthoringProjectServerFn = createServerFn({
  method: "GET",
})
  .middleware([authMiddleware])
  .inputValidator(zodValidator(SurveyAuthoringProjectIdSchema))
  .handler(async ({ data, context }): Promise<SurveyAuthoringProject> => {
    if (isMockMode()) {
      const { getMockSurveyProject } = await import("~/mock/surveyAuthoring");
      await mockDelay();
      const project = getMockSurveyProject(data.team, data.projectId);
      if (!project) throw new Error("Survey project not found");
      return project;
    }

    const { backendUrl, oboToken } = context as AuthContext;
    const response = await fetch(
      buildUrl(
        backendUrl,
        `/api/v1/intern/authoring/projects/${encodeURIComponent(data.projectId)}`,
        { team: data.team },
      ),
      { headers: getHeaders(oboToken) },
    );
    await handleApiResponse(response);
    return response.json() as Promise<SurveyAuthoringProject>;
  });

export const deleteSurveyAuthoringProjectServerFn = createServerFn({
  method: "POST",
})
  .middleware([authMiddleware])
  .inputValidator(zodValidator(SurveyAuthoringProjectIdSchema))
  .handler(async ({ data, context }): Promise<void> => {
    if (isMockMode()) {
      const { deleteMockSurveyProject } = await import(
        "~/mock/surveyAuthoring"
      );
      await mockDelay();
      if (!deleteMockSurveyProject(data.team, data.projectId)) {
        throw new Error("Survey project not found");
      }
      return;
    }

    const { backendUrl, oboToken } = context as AuthContext;
    const response = await fetch(
      buildUrl(
        backendUrl,
        `/api/v1/intern/authoring/projects/${encodeURIComponent(data.projectId)}`,
        { team: data.team },
      ),
      { method: "DELETE", headers: getHeaders(oboToken) },
    );
    await handleApiResponse(response);
  });

export const createSurveyAuthoringProjectServerFn = createServerFn({
  method: "POST",
})
  .middleware([authMiddleware])
  .inputValidator(zodValidator(CreateSurveyAuthoringProjectSchema))
  .handler(async ({ data, context }): Promise<SurveyAuthoringProject> => {
    if (isMockMode()) {
      const { createMockSurveyProject } = await import(
        "~/mock/surveyAuthoring"
      );
      await mockDelay();
      return createMockSurveyProject(
        data as Parameters<typeof createMockSurveyProject>[0],
      );
    }

    const { backendUrl, oboToken } = context as AuthContext;
    const response = await fetch(
      buildUrl(backendUrl, "/api/v1/intern/authoring/projects", {
        team: data.team,
      }),
      {
        method: "POST",
        headers: getHeaders(oboToken),
        body: JSON.stringify({
          name: data.name,
          surveyId: data.surveyId,
          document: data.document,
        }),
      },
    );
    await handleApiResponse(response);
    return response.json() as Promise<SurveyAuthoringProject>;
  });

export const saveSurveyAuthoringDraftServerFn = createServerFn({
  method: "POST",
})
  .middleware([authMiddleware])
  .inputValidator(zodValidator(SaveSurveyAuthoringDraftSchema))
  .handler(async ({ data, context }): Promise<SurveyAuthoringProject> => {
    if (isMockMode()) {
      const { saveMockSurveyProject } = await import("~/mock/surveyAuthoring");
      await mockDelay();
      return saveMockSurveyProject(
        data as Parameters<typeof saveMockSurveyProject>[0],
      );
    }

    const { backendUrl, oboToken } = context as AuthContext;
    const response = await fetch(
      buildUrl(
        backendUrl,
        `/api/v1/intern/authoring/projects/${encodeURIComponent(data.projectId)}/draft`,
        { team: data.team },
      ),
      {
        method: "PUT",
        headers: getHeaders(oboToken),
        body: JSON.stringify({
          expectedVersion: data.expectedVersion,
          name: data.name,
          surveyId: data.surveyId,
          document: data.document,
        }),
      },
    );
    await handleApiResponse(response);
    return response.json() as Promise<SurveyAuthoringProject>;
  });

export const fetchSurveyAuthoringRevisionsServerFn = createServerFn({
  method: "GET",
})
  .middleware([authMiddleware])
  .inputValidator(zodValidator(SurveyAuthoringProjectIdSchema))
  .handler(
    async ({ data, context }): Promise<SurveyAuthoringRevisionSummary[]> => {
      if (isMockMode()) {
        const { listMockSurveyRevisions } = await import(
          "~/mock/surveyAuthoring"
        );
        await mockDelay();
        return listMockSurveyRevisions(data.team, data.projectId);
      }

      const { backendUrl, oboToken } = context as AuthContext;
      const response = await fetch(
        buildUrl(
          backendUrl,
          `/api/v1/intern/authoring/projects/${encodeURIComponent(data.projectId)}/revisions`,
          { team: data.team },
        ),
        { headers: getHeaders(oboToken) },
      );
      await handleApiResponse(response);
      return response.json() as Promise<SurveyAuthoringRevisionSummary[]>;
    },
  );

export const createSurveyAuthoringRevisionServerFn = createServerFn({
  method: "POST",
})
  .middleware([authMiddleware])
  .inputValidator(zodValidator(CreateSurveyAuthoringRevisionSchema))
  .handler(async ({ data, context }): Promise<SurveyAuthoringRevision> => {
    if (isMockMode()) {
      const { createMockSurveyRevision } = await import(
        "~/mock/surveyAuthoring"
      );
      await mockDelay();
      return createMockSurveyRevision(data);
    }

    const { backendUrl, oboToken } = context as AuthContext;
    const response = await fetch(
      buildUrl(
        backendUrl,
        `/api/v1/intern/authoring/projects/${encodeURIComponent(data.projectId)}/revisions`,
        { team: data.team },
      ),
      {
        method: "POST",
        headers: getHeaders(oboToken),
        body: JSON.stringify({
          expectedDraftVersion: data.expectedDraftVersion,
        }),
      },
    );
    await handleApiResponse(response);
    return response.json() as Promise<SurveyAuthoringRevision>;
  });

export const fetchSurveyAuthoringRevisionServerFn = createServerFn({
  method: "GET",
})
  .middleware([authMiddleware])
  .inputValidator(zodValidator(SurveyAuthoringRevisionIdSchema))
  .handler(
    async ({ data, context }): Promise<SurveyAuthoringRevisionDetail> => {
      if (isMockMode()) {
        const { getMockSurveyRevisionDetail } = await import(
          "~/mock/surveyAuthoring"
        );
        await mockDelay();
        const detail = getMockSurveyRevisionDetail(data.team, data.revisionId);
        if (!detail) throw new Error("Survey revision not found");
        return detail;
      }

      const { backendUrl, oboToken } = context as AuthContext;
      const response = await fetch(
        buildUrl(
          backendUrl,
          `/api/v1/intern/authoring/revisions/${encodeURIComponent(data.revisionId)}`,
          { team: data.team },
        ),
        { headers: getHeaders(oboToken) },
      );
      await handleApiResponse(response);
      return response.json() as Promise<SurveyAuthoringRevisionDetail>;
    },
  );
